<template>
<div class="native-wizard">
  <!-- Step indicators -->
  <div class="wizard-steps">
    <div
      v-for="(step, index) in steps"
      :key="index"
      :class="['wizard-step', { active: currentStep === index, completed: currentStep > index }]"
    >
      <div class="step-icon-container">
        <i :class="getStepIcon(index)"></i>
      </div>
      <span class="step-title">{{ step }}</span>
    </div>
  </div>

  <!-- Step content -->
  <div class="wizard-content">
    <!-- Step 0: Choose parameter -->
    <div v-if="currentStep === 0" class="wizard-parameter-tab">
      <div class="parameters-section">
        <div
          v-for="(activeParameter, index) in activeParameters"
          :key="activeParameter.id"
          class="wizard-parameter-item"
        >
          <div class="parameter-header">
            <span class="parameter-label">Parameter {{ index + 1 }}</span>
            <button
              v-if="activeParameters.length > 1"
              :id="'btn-remove-' + index"
              :class="buttonClass"
              @click="removeParameter($event, index)"
              class="remove-btn">
              <i class="fa fa-minus"></i>
            </button>
          </div>
          <div class="wizard-parameter-container">
            <div class="wizard-parameter-content">
              <select required @change="setParameter($event, index)" class="wg-form-control">
                <option
                  v-for="availableParameter in availableParametersArray"
                  :disabled="isUnavailableParameter(availableParameter.id)"
                  :selected="availableParameter.id === activeParameter.id"
                  :key="availableParameter.id"
                  :value="availableParameter.id"
                >
                {{ availableParameter.name }}
                </option>
              </select>
              <select
                required
                @change="setParameterComparisonType($event, index)"
                v-model="activeParameter.threshold.comparisonType"
                class="wg-form-control">
                <option v-for="comparisonType in comparisonTypes" :key="comparisonType" :value="comparisonType">
                {{ camelCaseToSentenceCase(comparisonType) }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="checkIfMaxParameters" class="wizard-parameter-add-item">
          <button
            id="wizard-btn-add-parameter"
            :class="buttonClass"
            @click="addNewParameter($event)"
            class="add-parameter-btn">
            <i class="fa fa-plus"></i> Add Parameter
          </button>
        </div>
      </div>
    </div>

    <!-- Step 1: Configure thresholds -->
    <div v-if="currentStep === 1" class="wizard-thresholds-tab">
      <div v-if="activeParameters.length > 1">
        <h4>Logical Operator:</h4>
        <select
          required
          v-model="activeHazard.logicalOperator"
          class="wg-form-control">
          <option v-for="operator in logicalOperators" :key="operator" :value="operator">
            {{ camelCaseToSentenceCase(operator) }}
          </option>
        </select>
      </div>
      <div v-for="ap in activeParameters" :key="ap.id">
        <h4>{{ ap.name }}</h4>
        <parameter-config-chart
          @update-from-chart-event="updateFromChart"
          :key="componentKey"
          :parameter="ap"
          :parameterInfo="availableParameters[ap.id]"
          :units="availableParameters[ap.id].threshold.units"
          :displayUnits="unitSet[ap.id]"
        >
        </parameter-config-chart>
      </div>
    </div>

    <!-- Step 2: Select regions -->
    <div v-if="currentStep === 2" class="wizard-regions-tab">
      <p>Select what regions this hazard will apply to:</p>
      <div class="regions-list">
        <div v-for="region in regions" :key="region.id" class="region-checkbox">
          <input
            :id="'cb-wizard-region-' + region.id"
            type="checkbox"
            :checked="activeCheckedRegionIds.includes(region.id)"
            @change="updateCheckedRegionIds($event, region.id)"
          />
          <label :for="'cb-wizard-region-' + region.id">{{ region.name }}</label>
        </div>
      </div>
    </div>

    <!-- Step 3: Choose name -->
    <div v-if="currentStep === 3" class="wizard-name-tab">
      <label for="inputHazardName">Hazard Name:</label>
      <input
        id="inputHazardName"
        placeholder="Enter a unique name"
        maxlength="30"
        required
        type="text"
        v-model="activeHazard.name"
        class="wg-form-control"
      />
    </div>

    <!-- Step 4: Review -->
    <div v-if="currentStep === 4" class="wizard-review-tab">
      <table class="wizard-review-detail-table">
        <tbody>
        <tr>
          <td><strong>Name:</strong></td>
          <td>{{ activeHazard.name }}</td>
        </tr>
        <tr>
          <td><strong>Regions:</strong></td>
          <td>{{ checkedRegionNamesText }}</td>
        </tr>
        <tr v-if="activeParameters.length > 1">
          <td><strong>Logical Operator:</strong></td>
          <td>{{ camelCaseToSentenceCase(activeHazard.logicalOperator) }}</td>
        </tr>
        </tbody>
      </table>
      <table class="wizard-parameter-table">
      <thead>
        <tr>
          <th>Parameter</th>
          <th>Threshold</th>
          <th>Sensitivity</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="ap in activeParameters" :key="ap.id">
          <td>{{ ap.name }}</td>
          <td>{{ computeTableRow(ap) }}</td>
          <td>{{ convertSensitivity(ap.threshold.sigma, ap.threshold.units, unitSet[ap.id], ap.threshold.precision) }} {{ formatDisplayUnit(unitSet[ap.id]) }}</td>
        </tr>
      </tbody>
      </table>
    </div>
  </div>

  <!-- Navigation buttons -->
  <div class="wizard-buttons">
    <button
    @click="cancelWizard"
    class="wizard-btn wizard-btn-cancel">
    Cancel
  </button>
  <button
    v-if="currentStep > 0"
    @click="previousStep"
    class="wizard-btn wizard-btn-secondary">
    Previous
  </button>
  <button
      v-if="currentStep < steps.length - 1"
      @click="nextStep"
      class="wizard-btn wizard-btn-primary">
      Next
    </button>
    <button
      v-if="currentStep === steps.length - 1"
      @click="complete"
      class="wizard-btn wizard-btn-success">
      Complete
    </button>
  </div>
</div>
</template>

<script>
import ParameterConfigChart from './ParameterConfigChart.vue';
import { UnitConverter } from '../js/unitConverter.js';
import { SensitivityConverter } from '../js/sensitivityConverter.js';
import LoadingSpinner from './LoadingSpinner.vue';
import swal from 'sweetalert';
import { formatComparisonType,formatDisplayUnit } from '../js/helperFunctions.js';
import unitSets from '../constants/unitSets.json';

const unitConverter = new UnitConverter();
const sensitivityConverter = new SensitivityConverter();
const _MAX_PARAMETERS = 4;
export default {
  components: {
    LoadingSpinner,
    ParameterConfigChart
  },
  props: [
    'title',
    'hazardNames',
    'regions',
    'availableParametersArray',
    'initHazard',
    'newHazard',
    'initParameters'
  ],
  data() {
    return {

      unitSets: JSON.parse(JSON.stringify(unitSets)),

      // inits
      readOnly: false,
      loadingWizard: false,
      activeHazard: this.initHazard,
      startNewHazard:  this.newHazard,
      activeHazardOriginal: JSON.parse(JSON.stringify(this.initHazard)),
      activeCheckedRegionIds: this.initHazard.regions ? this.initHazard.regions : [],
      activeParameters: this.initParameters,

      // static
      comparisonTypes: ['greaterThan', 'lessThan', 'between'],
      logicalOperators: ['or', 'and'],
      steps: ['Choose parameter', 'Configure thresholds', 'Select regions', 'Choose name', 'Review'],
      currentStep: 0,

      // misc dynamic
      componentKey: 0
    }
  },
  computed: {
    config() { return this.$store.getters.config; },
    unitSetId() { return this.config.unitSetId; },
    unitSet() { return this.unitSets[this.unitSetId]; },
    between() { return this.activeHazard.threshold.comparisonType === 'between'; },
    checkedRegionNames() { return this.activeCheckedRegionIds.map(id => this.regions[id].name); },
    checkedRegionNamesText() { return this.arrayToSentence(this.checkedRegionNames); },
    computedActiveHazardName() { return this.activeHazard.name; },
    buttonClass() { return this.readOnly ? 'hc-btn-offlimits' : 'hc-btn'; },
    checkIfMaxParameters() { return this.activeParameters.length < _MAX_PARAMETERS ? true : false },
    availableParameters() { return JSON.parse(JSON.stringify(this.$store.getters.parameterConfig)); }
  },
  methods: {
    getStepIcon(index) {
      const icons = [
        'ti ti-cloud',
        'ti ti-settings',
        'ti ti-world',
        'ti ti-user',
        'ti ti-check'
      ];
      return icons[index] || 'ti ti-circle';
    },
    async nextStep() {
      // Validate current step before moving to next
      if (this.currentStep === 0) {
        // Parameter selection - auto-valid if at least one param
        if (this.activeParameters.length === 0) {
          swal('You must select at least one parameter to proceed.');
          return;
        }
        await this.prepForParameterConfigChart();
      } else if (this.currentStep === 1) {
        if (!this.validateThresholdInput()) return;
      } else if (this.currentStep === 2) {
        if (!this.validateHazardRegionInput()) return;
      } else if (this.currentStep === 3) {
        if (!this.validateHazardNameInput()) return;
        this.validateUniqueNames();
      }
      
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
      }
    },
    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    computeTableRow(ap) {
      const comparisonType = ap.threshold.comparisonType;
      const comparisonTypeStr = formatComparisonType(comparisonType);
      let muStr = '';
      if (comparisonType == 'between') {
        const mu1 = this.convertValue(ap.threshold.mu1, ap.threshold.units, this.unitSet[ap.id], ap.threshold.precision);
        const mu2 = this.convertValue(ap.threshold.mu2, ap.threshold.units, this.unitSet[ap.id], ap.threshold.precision);
        muStr = `${mu1} and ${mu2}`;
      }
      else {
        const mu = this.convertValue(ap.threshold.mu, ap.threshold.units, this.unitSet[ap.id], ap.threshold.precision);
        muStr = `${mu}`
      }
      const units = this.formatDisplayUnit(this.unitSet[ap.id]);
      const str = `${comparisonTypeStr} ${muStr} ${units}`;
      return str;
    },
    isUnavailableParameter(parameterId) {
      const nonSelectedParamters = this.getParametersNotSelected();
      const nonSelectedParameterIds = nonSelectedParamters.map(param => param.id);
      return !nonSelectedParameterIds.includes(parameterId);
    },
    getParametersNotSelected() { // parameters not currently selected
      const allParameters = JSON.parse(JSON.stringify(this.availableParametersArray));
      const currentlySelectedParametersIds = this.activeParameters.map(param => param.id);
      const selectableParameters = allParameters.filter(param => !currentlySelectedParametersIds.includes(param.id));
      return selectableParameters;
    },
    convertValue(valueIn, unitsIn, unitsOut, precision = 1) {
      const valueOut = unitConverter.convert(valueIn, unitsIn, unitsOut, precision);
      return +valueOut;
    },
    convertSensitivity(valueIn, unitsIn, unitsOut, precision = 1) {
      const valueOut = sensitivityConverter.convert(valueIn, unitsIn, unitsOut, precision);
      return +valueOut;
    },
    setLoading() {
      this.loadingWizard = !this.loadingWizard;
    },
    flipComponentKey() {
      this.componentKey = !this.componentKey; // forces reload of component
    },
    prepForParameterConfigChart() {
      const apLength = this.activeParameters.length;
      for (let i = 0; i < apLength; ++i) {
        const param = this.availableParametersArray.find( p => p.id === this.activeParameters[i].id );
        if (param) {
          const newComparisonType = this.activeParameters[i].threshold.comparisonType;
          this.activeParameters[i] = JSON.parse(JSON.stringify(param)); //assigning copy of object instead of reference
          this.activeParameters[i].threshold.comparisonType = newComparisonType;
          if (Object.keys(this.activeHazardOriginal).length > 0) { //checking if parameter is being edited
            this.activeParameters[i].threshold.mu = this.activeHazardOriginal.parameters.hasOwnProperty(param.id) ? 
                    this.activeHazardOriginal.parameters[param.id].threshold.mu :
                    this.activeParameters[i].threshold.mu;
            this.activeParameters[i].threshold.sigma = this.activeHazardOriginal.parameters.hasOwnProperty(param.id) ? 
                    this.activeHazardOriginal.parameters[param.id].threshold.sigma :
                    this.activeParameters[i].threshold.sigma;
          }
        }
      }
      // setting logical operator
      if (this.activeHazard.logicalOperator == null && this.activeParameters.length > 1) {
        this.activeHazard.logicalOperator = this.logicalOperators[0];
      }
      this.flipComponentKey();
      // To disable next button until next view is fully ready
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve(true);
        }, 500)
      })
    },
    camelCaseToSentenceCase(s) {
      if (s == null) {
        return;
      }
      const result = s.replace(/([A-Z])/g, " $1");
      const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
      return finalResult;
    },
    arrayToSentence(array) {
      return array
        .join(", ")
        .replace(/, ((?:.(?!, ))+)$/, ' and $1');
    },
    addNewParameter() {
      if (this.activeParameters.length < _MAX_PARAMETERS && this.availableParametersArray.length > 0) {
        const tmpParameter = JSON.parse(JSON.stringify(this.getParametersNotSelected()[0]));
        this.activeParameters.push(tmpParameter);
      }
      this.flipComponentKey(); // reload the parameter config chart component
    },
    removeParameter(event, index) {
      // checking if it's trying remove non existant parameters or if it is trying to remove the only parameter
      if (this.activeParameters.length -1 < index || (index != 0 && this.activeParameters.length === 1)) {
        return;
      }
      this.activeParameters.splice(index, 1);
      this.flipComponentKey() // reload the parameter config chart component
    },
    setParameter(event, parameterNumber) {
      if (this.activeParameters[parameterNumber] == null) {
        return;
      }
      const parameter = this.availableParametersArray.find( x => x.id === event.target.value );
      this.activeParameters[parameterNumber] = JSON.parse(JSON.stringify(parameter));
      this.flipComponentKey() // reload the parameter config chart component
    },
    setParameterComparisonType(event, parameterNumber) {
      if (this.activeParameters[parameterNumber] == null) {
        return;
      }
      this.activeParameters[parameterNumber].threshold.comparisonType = event.target.value;
      this.flipComponentKey(); // reload the parameter config chart component
    },
    updateCheckedRegionIds(event, regionId) {
      if (event.target.checked) {
        if (!this.activeCheckedRegionIds.includes(regionId)) {
          this.activeCheckedRegionIds.push(regionId);
        }
      }
      else {
        if (this.activeCheckedRegionIds.includes(regionId)) {
          this.activeCheckedRegionIds = this.activeCheckedRegionIds.filter(e => e !== regionId);
        }
      }
    },
    validateHazardNameInput() {
      const sIn = document.getElementById('inputHazardName').value;
      const alphanumericCheck = /\d|[A-z]/;
      if ([null, undefined, '', " ", ""].includes(sIn) || sIn.includes(":") || !alphanumericCheck.test(sIn) ) {
        swal('You must enter a valid name to proceed');
        return false;
      }

      const names = this.hazardNames.filter((item) => item === sIn);

      if (this.startNewHazard && names.length > 0) {
        swal('You must enter a unique name to proceed');
        return false;
      }

      if (!this.startNewHazard && names.length > 1) {
        swal('You must enter a unique name to proceed');
        return false;
      }
      this.flipComponentKey();
      return true;
    },
    validateHazardRegionInput() {
      const rIn = this.checkedRegionNames;
      if (rIn.length > 0) {
        return true;
      }
      else {
        swal('You must select  at least one region to proceed.');
        return false;
      }
    },
    validateUniqueNames() {
      if (this.startNewHazard) {
        document.getElementById('inputHazardName').value = '';
      }
    },
    validateThresholdInput() {
      const numberCheck =  /^[+-]?\d+(\.\d+)?$/;
      const isNotValidNumber = this.activeParameters.find((x) => !numberCheck.test(x.threshold.mu) || !numberCheck.test(x.threshold.sigma) );

      if (isNotValidNumber) {
        swal('You must enter only valid numbers to proceed.');
        return false;
      }

      const isGreaterThanMax = this.activeParameters.find(x => x.threshold.mu > x.threshold.maxMu || x.threshold.sigma > x.threshold.maxSigma );
      const isLessThanMin = this.activeParameters.find(x => x.threshold.mu < x.threshold.minMu || x.threshold.sigma < x.threshold.minSigma );

      if (isLessThanMin || isGreaterThanMax) {
        swal('You can only enter numbers between max and min thresholds to proceed.');
        return false;
      }

      return true;
    },
    hide() {
      this.$emit('child-hide-event');
    },
    convertHazardNameToHazardId(hazardName) {
      const hazardId = hazardName.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
      return hazardId;
    },
    constructHazardObjFromInput() {
      const hazardObj = {};
      hazardObj.id = this.startNewHazard ? this.convertHazardNameToHazardId(this.activeHazard.name) : this.activeHazard.id;
      hazardObj.name = this.activeHazard.name;
      hazardObj.logicalOperator = this.activeHazard.logicalOperator;
      hazardObj.regions = this.activeCheckedRegionIds;
      hazardObj.enabled = (this.activeHazard.enabled || !this.activeHazard.hasOwnProperty("enabled")) ? true : false;
      hazardObj.parameters = {};
      for (let i = 0; i < this.activeParameters.length; i++) {
        const param = JSON.parse(JSON.stringify(this.activeParameters[i]));
        hazardObj.parameters[this.activeParameters[i].id] = (({ id, threshold }) => ({ id, threshold }))(param);
        hazardObj.parameters[this.activeParameters[i].id].threshold = (({ mu, mu1, mu2, sigma, displayUnits, precision, comparisonType }) => ({ mu, mu1, mu2, sigma, displayUnits, precision, comparisonType }))(param.threshold);
      }
      return hazardObj;
    },
    complete() {
      const hazardObj = this.constructHazardObjFromInput();
      this.hazardObj = hazardObj;
      this.$emit('hazard-complete-event', hazardObj, this.initHazard.id);
      this.hide();
      this.loadingWizard = true;
    },
    updateFromChart(parameter, mu, mu1, mu2, sigma) {
      const ap = this.activeParameters.find(x => x.id === parameter.id);
      ap.threshold.mu = mu;
      ap.threshold.mu1 = mu1;
      ap.threshold.mu2 = mu2;
      ap.threshold.sigma = sigma;
    },
    cancelWizard() {
      this.$emit('hazard-cancel-event', this.activeHazardOriginal, this.activeHazardOriginal.id);
    },
    formatDisplayUnit: formatDisplayUnit,
    formatComparisonType: formatComparisonType
  }
}

</script>

<style scoped>
.native-wizard {
  background: white;
  border-radius: 4px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.wizard-steps {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}

.wizard-step {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  opacity: 0.4;
  color: #95a5a6;
  text-align: center;
}

.wizard-step.active {
  opacity: 1;
  color: #3498db;
}

.wizard-step.completed {
  opacity: 1;
  color: #27ae60;
}

.wizard-step:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 20px;
  left: calc(50% + 22px);
  width: calc(100% - 47px);
  height: 2px;
  background: #ecf0f1;
  z-index: 0;
  transition: background 0.4s ease, box-shadow 0.4s ease;
}

.wizard-step.completed:not(:last-child)::after {
  background: #27ae60;
  animation: popLine 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  box-shadow: 0 2px 2px rgba(39, 174, 96, 0.3);
}

@keyframes popLine {
  0% {
    width: 0%;
  }
  60% {
    width: calc(100% + 6px);
  }
  100% {
    width: calc(100% - 47px);
  }
}

.step-icon-container {
  min-width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #ecf0f1;
  font-size: 24px;
  font-weight: 600;
  position: relative;
  z-index: 1;
}

.wizard-step.active .step-icon-container {
  background: #3498db;
  color: white;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
}

.wizard-step.completed .step-icon-container {
  background: #27ae60;
  color: white;
}

.wizard-step .step-title {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  padding: 0 4px;
}

.wizard-content {
  min-height: 300px;
  padding: 30px 20px;
  background: #fafafa;
  border-radius: 4px;
  margin-bottom: 20px;
}

.wizard-parameter-tab,
.wizard-thresholds-tab,
.wizard-regions-tab,
.wizard-name-tab,
.wizard-review-tab {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.parameters-section {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  align-items: flex-start;
}

.parameter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.parameter-label {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
}

.remove-btn {
  padding: 2px 6px;
  font-size: 11px;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
}

.remove-btn:hover {
  background: #c0392b;
}

.wizard-parameter-item {
  flex: 0 1 calc(25% - 12px);
  min-width: 200px;
  background: white;
  padding: 15px;
  border-radius: 4px;
  border-left: 3px solid #16a085;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.wizard-parameter-add-item {
  flex: 0 1 calc(25% - 12px);
  min-width: 200px;
  background: #fafafa;
  padding: 15px;
  border-radius: 4px;
  border: 2px dashed #d0d0d0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-parameter-btn {
  width: 100%;
  padding: 30px 15px;
  border: 2px dashed #3498db;
  background: white;
  color: #3498db;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.add-parameter-btn:hover {
  background: #e3f2fd;
  border-color: #2980b9;
  color: #2980b9;
}

.wizard-parameter-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wizard-parameter-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wizard-parameter-content select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.wizard-regions-tab p {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 14px;
  font-weight: 500;
}

.regions-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.region-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.region-checkbox:hover {
  background-color: #f5f5f5;
}

.region-checkbox input[type="checkbox"] {
  cursor: pointer;
  width: 18px;
  height: 18px;
}

.region-checkbox label {
  margin: 0;
  cursor: pointer;
  flex: 1;
  font-size: 14px;
  color: #2c3e50;
}

.wizard-name-tab label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
}

.wizard-name-tab .wg-form-control {
  max-width: 400px;
}

.wizard-review-detail-table,
.wizard-parameter-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
}

.wizard-review-detail-table tbody tr {
  border-bottom: 1px solid #ecf0f1;
}

.wizard-review-detail-table tbody tr:last-child {
  border-bottom: none;
}

.wizard-review-detail-table td,
.wizard-parameter-table td,
.wizard-parameter-table th {
  padding: 12px 15px;
  text-align: left;
}

.wizard-review-detail-table td:first-child {
  font-weight: 600;
  color: #2c3e50;
  width: 120px;
}

.wizard-parameter-table th {
  background: #ecf0f1;
  font-weight: 600;
  color: #2c3e50;
  font-size: 13px;
}

.wizard-parameter-table tbody tr {
  border-bottom: 1px solid #ecf0f1;
}

.wizard-parameter-table tbody tr:last-child {
  border-bottom: none;
}

.wizard-buttons {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #ecf0f1;
}

.wizard-btn {
  padding: 10px 24px;
  border: 1px solid;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  min-width: 100px;
}

.wizard-btn:hover {
  opacity: 0.9;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.wizard-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wizard-btn-primary {
  background-color: #3498db;
  border-color: #3498db;
  color: white;
}

.wizard-btn-secondary {
  background-color: #95a5a6;
  border-color: #95a5a6;
  color: white;
}

.wizard-btn-success {
  background-color: #27ae60;
  border-color: #27ae60;
  color: white;
}

.wizard-btn-cancel {
  background-color: #fadbd8;
  border-color: #e74c3c;
  color: #c0392b;
}

.wg-form-control {
  padding: 8px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
}

.wg-form-control:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
}

h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}
</style>
